import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
  Text,
} from 'react-aria-components';
import { toast } from 'react-toastify';
import { Icon } from 'src/components/Icon';
import MagicBriefMajorError from 'src/assets/svgicons/magicbrief/MagicBriefMajorError.svg';
import ChevronUp from 'src/assets/svgicons/duocolor/chevron-up.svg';
import Copy01 from 'src/assets/svgicons/line/copy-06.svg';
import { MagicBriefButton } from 'src/components/Button/MagicBriefButton';

type Props = {
  error: Error;
};

const DefaultFallback: React.FunctionComponent<Props> = ({
  error,
}): JSX.Element => {
  return (
    <div className="flex flex-1 flex-col items-center gap-[124px] p-4">
      <div className="flex grow-0 flex-col items-center gap-6">
        <div className="flex flex-col place-items-center gap-8">
          <MagicBriefMajorError className="max-w-64" />
          <div className="flex flex-col items-center">
            <Heading className="text-xl font-semibold text-primary">
              You&apos;ve encountered an error
            </Heading>
            <Text className="max-w-96 text-center text-sm text-text-secondary">
              Our team has been notified and is working on a fix. Contact
              support if you need assistance
            </Text>
          </div>
        </div>

        <div className="flex flex-row gap-4">
          <MagicBriefButton
            variant="secondary"
            className="w-max"
            onPress={() => window.location.replace('/')}
          >
            Home
          </MagicBriefButton>
          <MagicBriefButton
            className="w-max"
            onPress={() => window.location.reload()}
          >
            Attempt Reload
          </MagicBriefButton>
        </div>
      </div>

      <Disclosure className="group w-full max-w-[880px] rounded-md border border-solid border-gray-100 bg-white">
        <Heading className="flex flex-row items-center justify-between pr-4">
          <Button
            slot="trigger"
            className="flex w-full flex-1 items-center gap-2.5 rounded-lg px-4 py-3 text-left text-sm text-gray-700 focus:outline-none"
          >
            <Icon className="size-5 group-expanded:rotate-180">
              <ChevronUp />
            </Icon>
            <span>Technical Details</span>
          </Button>

          <Button
            className="rounded p-1 text-left text-sm text-gray-500 hover:bg-gray-100 focus:outline-none"
            onPress={() => {
              navigator.clipboard
                .writeText(error.stack ?? error.message)
                .then(() => {
                  toast.success('Error trace copied to clipboard', {
                    className: 'toast-success ',
                  });
                })
                .catch(() => {
                  toast('Could not copy error to clipboard.', {
                    className: 'toast-danger',
                  });
                });
            }}
          >
            <Icon className="size-5">
              <Copy01 />
            </Icon>
          </Button>
        </Heading>
        <DisclosurePanel className="text-sm">
          <div className="overflow-auto p-4">
            <div className="mb-4">
              <h2 className="mb-1 font-semibold text-gray-700">Message</h2>
              <p className="pre rounded-md bg-gray-100/50 p-2.5 font-mono text-gray-500">
                {error.message}
              </p>
            </div>

            <div>
              <h2 className="mb-1 font-semibold text-gray-700">Stack Trace</h2>
              <pre className="pre overflow-auto rounded-md bg-gray-100/50 p-2.5 font-mono text-gray-500">
                {error.stack || 'No stack trace'}
              </pre>
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
};

export default DefaultFallback;
